import { ActionIcon, Button, Group, Stack, Table, TextInput, Title } from "@mantine/core";
import { useField } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { to, voiding } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";

const Page = () => {
    const { data } = useSuspenseQuery(queries.allPhoneNumbers);
    const deletePhoneNumber = useGraphqlMutation(
        graphql(`
            mutation DevtoolsSystemwidePhoneNumbersDeletePhoneNumber($phoneNumber: String!) {
                support {
                    deletePhoneNumber(phoneNumber: $phoneNumber) {
                        phoneNumber
                    }
                }
            }
        `),
    );

    function handleNewSystemwidePhoneNumber() {
        const Panel = () => {
            const name = useField({ initialValue: "", validate: (it) => (it.trim().length == 0 ? "Required" : null) });
            const phoneNumber = useField({ initialValue: "", validate: (it) => (it.trim().length == 0 ? "Required" : null) });

            const save = useGraphqlMutation(
                graphql(`
                    mutation DevtoolsCreateSystemwidePhoneNumber($phoneNumber: String!, $name: String!) {
                        support {
                            createPhoneNumber(phoneNumber: $phoneNumber, name: $name) {
                                phoneNumber
                            }
                        }
                    }
                `),
            );

            async function create() {
                await name.validate();
                await phoneNumber.validate();

                if (!!name.error || !!phoneNumber.error) return;

                await save.mutateAsync({ name: name.getValue(), phoneNumber: phoneNumber.getValue() });
                notify.show.success("Phone Number Created");

                await qk.invalidate("support", "phone-numbers");
                modals.closeAll();
            }

            return (
                <Stack>
                    <TextInput label="Name" {...name.getInputProps()} />
                    <TextInput label="Phone Number" {...phoneNumber.getInputProps()} />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={modals.closeAll}>
                            Cancel
                        </Button>
                        <Button onClick={voiding(create)}>Create</Button>
                    </Group>
                </Stack>
            );
        };

        modals.open({
            title: <Title order={3}>Create Systemwide Phone Number</Title>,
            children: <Panel />,
        });
    }

    return (
        <Content paper>
            <Content.Heading backable>Systemwide Phone Numbers</Content.Heading>
            <Content.Action>
                <Button variant="default" onClick={handleNewSystemwidePhoneNumber} leftSection={<IconPlus size={16} />}>
                    Create
                </Button>
            </Content.Action>

            <Table striped>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Phone Number</Table.Th>
                        <Table.Th />
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.phoneNumbers.map((each) => (
                        <Table.Tr key={each.phoneNumber}>
                            <Table.Td>{each.name}</Table.Td>
                            <Table.Td>{each.phoneNumber}</Table.Td>
                            <Table.Td ta="right">
                                <ActionIcon
                                    variant="subtle"
                                    c="red"
                                    onClick={voiding(async () => {
                                        const { close } = await notify.confirm("Are you sure you want to delete this phone number?");
                                        await deletePhoneNumber.mutateAsync(each);
                                        await qk.invalidate("support", "phone-numbers");
                                        close();
                                    })}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Content>
    );
};

const queries = {
    allPhoneNumbers: queryOptions({
        queryKey: qk("support", "phone-numbers"),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query SupportPhoneNumbers {
                        support {
                            phoneNumbers {
                                phoneNumber
                                name
                            }
                        }
                    }
                `),
            ),

        select: to("support"),
    }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/systemwide-phonenumbers")({
    component: Page,
});
